/* Newsletter — sections/newsletter.liquid */

class NewsletterForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    if (!this.form) return;

    this.input = this.form.querySelector('input[type="email"]');
    this.error = this.form.querySelector('[aria-live="assertive"]');
    this.errorText = this.form.querySelector("[data-newsletter-error-text]");

    if (!this.input || !this.error || !this.errorText) return;

    this.invalidMessage = this.dataset.invalidMessage || "";

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInput = this.handleInput.bind(this);

    this.form.addEventListener("submit", this.handleSubmit);
    this.input.addEventListener("input", this.handleInput);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.handleSubmit);
    this.input?.removeEventListener("input", this.handleInput);
  }

  handleSubmit(event) {
    if (this.isValid()) {
      this.clearError();
      return;
    }

    event.preventDefault();
    this.showError();
  }


  handleInput() {
    if (this.input.getAttribute("aria-invalid") !== "true") return;
    if (this.isValid()) this.clearError();
  }

  isValid() {
    const value = this.input.value.trim();
    if (!value) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  showError() {
    const message = this.invalidMessage || this.input.validationMessage;

    this.input.setAttribute("aria-invalid", "true");
    this.error.classList.remove("hidden");
    this.errorText.textContent = "";

    requestAnimationFrame(() => {
      this.errorText.textContent = message;
    });

    this.input.focus({ preventScroll: false });
  }

  clearError() {
    this.input.removeAttribute("aria-invalid");
    this.errorText.textContent = "";
    this.error.classList.add("hidden");
  }
}

if (!customElements.get("newsletter-form")) {
  customElements.define("newsletter-form", NewsletterForm);
}

/* "Move images on scroll": shifts each image vertically as the section passes
   through the viewport. data-direction="1" moves down, "-1" moves up. */
class NewsletterFloat extends HTMLElement {
  connectedCallback() {
    if (this.dataset.motion !== "true") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.direction = Number(this.dataset.direction) || 1;
    this.section = this.closest(".newsletter_section_inner") || this;
    this.raf = null;
    this.inView = false;

    this.requestUpdate = this.requestUpdate.bind(this);
    this.update = this.update.bind(this);

    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      if (this.inView) this.requestUpdate();
    });
    this.observer.observe(this.section);

    window.addEventListener("scroll", this.requestUpdate, { passive: true });
    window.addEventListener("resize", this.requestUpdate, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener("scroll", this.requestUpdate);
    window.removeEventListener("resize", this.requestUpdate);
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  requestUpdate() {
    if (!this.inView || this.raf) return;
    this.raf = requestAnimationFrame(this.update);
  }

  update() {
    this.raf = null;
    const rect = this.section.getBoundingClientRect();
    const viewport = window.innerHeight;

    // 0 when the section enters at the bottom, 1 when it leaves at the top
    const progress = Math.min(1, Math.max(0, (viewport - rect.top) / (viewport + rect.height)));
    const isMobile = window.innerWidth < 750;
    const range = isMobile ? 60 : 90;
    const shift = (progress - 0.5) * range * this.direction;

    this.style.setProperty("--nl-shift", isMobile ? "0px" : `${shift.toFixed(1)}px`);
    this.style.setProperty("--nl-shift-x", isMobile ? `${shift.toFixed(1)}px` : "0px");
  }
}

if (!customElements.get("newsletter-float")) {
  customElements.define("newsletter-float", NewsletterFloat);
}
